"use no memo";

// @tanstack/react-table v8 keeps its state in a mutable `table` instance whose identity is stable
// across updates, so React Compiler memoization would skip re-renders that the table needs. Remove
// this opt-out when the table upgrades to v9, which is compiler-safe.
import type { ColumnDef, ColumnHelper, CellContext } from "@tanstack/react-table";
import { createColumnHelper } from "@tanstack/react-table";
import { formatDate, prettyDate } from "common/utils/dates";
import { formatDollars } from "common/utils/formatDollars";
import { camelCaseToTitleCase } from "common/utils/strings";
import type { HTMLAttributes } from "react";
import cx from "ui/cx";
import Checkbox from "ui/forms/Checkbox";
import styles from "./DataTable.module.scss";
import TableRowCell, { EditableCellContents } from "./TableCells";
import type { DataTableColumn, TableRowData } from "./types";

export function getColumnId<T>(column: Pick<DataTableColumn<T>, "id" | "field" | "header">): string {
  return column.id ?? (column.field as string) ?? column.header?.toString().toLowerCase().replace(/\s+/g, "-");
}
export function generateColDefs<T extends TableRowData = TableRowData>(
  columns: DataTableColumn<T>[],
  tableWidth: number | null,
  columnVisibility: Record<string, boolean> = {},
): ColumnDef<T>[] {
  const colHelper = createColumnHelper<T>();
  const visibleColumns = columns.filter((column) => columnVisibility[getColumnId(column)] !== false);

  return columns.map((column) => {
    const isVisible = columnVisibility[getColumnId(column)] !== false;
    return generateColDef(column, colHelper, {
      lastCol: column === visibleColumns[visibleColumns.length - 1],
      tableWidth: isVisible ? tableWidth ?? undefined : undefined,
    });
  });
}

// A helper function to translate some of our existing table APIs to react-table
export function generateColDef<T extends TableRowData = TableRowData>(
  column: DataTableColumn<T>,
  colHelper: ColumnHelper<T>,
  size: {
    lastCol: boolean;
    tableWidth?: number;
  },
): ColumnDef<T> {
  const {
    field,
    accessor,
    cell: customCell,
    type,
    fallback,
    bold,
    wrap,
    width,
    fullCell,
    editable,
    getEditable,
    disabled,
    getDisabled,
    selectOptions,
    required,
    getCellClassNames,
    columnLeadSeparator,
    cellClassNameOverrides,
    tooltip,
    ...columnDef
  } = column;

  const icon = columnDef.icon;

  const colDef: ColumnDef<T> = {
    id: getColumnId(column),
    header: columnDef.header ?? camelCaseToTitleCase(field as string),
    sortUndefined: 1, // Undefined values will be sorted with lower priority (descending)
    meta: {
      required,
      editable,
      type,
    },
  };

  // sets the starting width for columns with preset width (as fraction 0-1)
  // other columns get their size calculated by the DataTable effect
  if (width && size.tableWidth) {
    colDef.size = Math.floor(size.tableWidth * width);
  }

  let cellContents: ColumnDef<T>["cell"] = (cell) => cell.getValue();

  switch (type) {
    // fallback is default true for dates
    case "DATE": {
      cellContents = (cell) =>
        fallback !== false && !cell.getValue() ? cell.renderValue() : prettyDate(cell.getValue() as string | Date);
      break;
    }
    case "DATETIME": {
      cellContents = (cell) =>
        fallback !== false && !cell.getValue() ? cell.renderValue() : formatDate(cell.getValue() as Date, "WITH_TIME");
      break;
    }
    case "USD": {
      cellContents = (cell) => {
        if (typeof cell.getValue() !== "number") {
          return fallback ? cell.renderValue() : "";
        } else {
          return formatDollars(cell.getValue() as number);
        }
      };
      break;
    }

    case "BOOLEAN": {
      cellContents = (cell) =>
        !fallback && typeof cell.getValue() !== "boolean" ? (
          cell.renderValue()
        ) : (
          <Checkbox checked={Boolean(cell.getValue())} readOnly />
        );
      break;
    }

    default: {
      cellContents = (cell) => (!fallback ? cell.getValue() : (cell.renderValue() as {}).toString());
      break;
    }
  }

  if (customCell) {
    cellContents = customCell;
  }

  if (editable || getEditable !== undefined) {
    // TODO: expand to support more Editable cell types
    if (!type || type === "NUMBER" || type === "TEXT" || type === "USD" || type === "BOOLEAN" || type === "SELECT") {
      const currentCellContents = typeof cellContents === "string" ? () => cellContents : cellContents;

      cellContents = function EditableCell(cell: CellContext<T, unknown>) {
        // customCell is currently incompatible with editable, has not been tested in existing tables
        const mayEdit = !customCell && editable;
        // customCell can work with getEditable
        const maybeEditable = getEditable !== undefined && getEditable(cell);

        return mayEdit || maybeEditable ? (
          <EditableCellContents
            // forces a remount when adding a new row to the beginning
            key={String(cell.table.options.meta?.getRowId?.(cell.row.original) ?? cell.row.index)}
            {...cell}
            // TODO: revisit and support accessor for editable cells without this 'id' hack
            field={field || (columnDef.id as keyof T)}
            value={cell.getValue() as string | number}
            columnType={type || "TEXT"}
            selectOptions={selectOptions}
            disabled={getDisabled !== undefined ? getDisabled(cell) : disabled}
          />
        ) : (
          currentCellContents(cell)
        );
      };
    }
  }

  colDef.cell = (cell) => {
    const style: HTMLAttributes<HTMLDivElement>["style"] = {};

    // TODO - when enableResizing is false for a column, will need to do some complicated math, as resizing in the flex model
    // impacts adjacent columns, so behavior is not predictable.

    if (size.tableWidth) {
      style.width = cell.column.getSize();
    }
    return (
      <TableRowCell
        {...cell}
        isMobileView={!!cell.table.options.meta?.isMobileView}
        style={style}
        className={cx(
          {
            styles,
            bold,
            fullCell,
            columnLeadSeparator,
            editable: getEditable !== undefined ? getEditable(cell) : editable,
            columnClassNames: true,
            icon: icon !== undefined,
            dynamicCell: customCell !== undefined || editable,
            /* 
                Allow cells to manually opt in or out of the above classnames
                e.g.
                {
                   field: "foo",
                   cell: ({ row }) => row.original.foo.bar,
                   cellClassNameOverrides: { dynamicCell: false },
                }
                since the dynamicCell class is added for any column with a cell method
                but the class enables overflow visible, which may be undesirable
            */
            ...cellClassNameOverrides,
          },
          getCellClassNames?.(cell),
        )}
        // Skip rendering icon if value is empty. If you want to render an icon anyways, use `cell` prop
        icon={typeof icon === "function" && cellContents ? icon(cell) : null}
        tooltip={tooltip?.(cell)}
      >
        {typeof cellContents === "string" ? cellContents : cellContents(cell)}
      </TableRowCell>
    );
  };

  if (accessor) {
    return colHelper.accessor(accessor, {
      ...colDef,
      ...columnDef,
    });
  }

  return {
    accessorKey: field,
    ...colDef,
    ...columnDef,
  };
}
